function getUpdatedAtMs(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }
  return 0;
}

export function normalizeRsvpUid(row) {
  if (!row) return null;
  if (typeof row.userId === 'string' && row.userId.trim()) return row.userId.trim();
  if (typeof row.memberId === 'string' && row.memberId.trim()) return row.memberId.trim();
  if (typeof row.id === 'string' && row.id.includes('__')) return row.id.split('__')[0] || null;
  if (typeof row.id === 'string' && row.id.trim()) return row.id.trim();
  return null;
}

export function normalizeRsvpOccurrenceId(row) {
  if (!row) return null;
  if (typeof row.occurrenceId === 'string' && row.occurrenceId.trim()) return row.occurrenceId.trim();
  if (typeof row.id === 'string' && row.id.includes('__')) {
    const tail = row.id.split('__').slice(1).join('__').trim();
    return tail || null;
  }
  return null;
}

export function toRsvpRowFromSnapshot(snap) {
  if (!snap) return null;
  return {
    id: snap.id,
    ...(typeof snap.data === 'function' ? snap.data() : {}),
  };
}

function pickLatestByUpdatedAt(current, candidate) {
  if (!current) return candidate;
  return getUpdatedAtMs(candidate?.updatedAt) >= getUpdatedAtMs(current?.updatedAt) ? candidate : current;
}

function decorateRsvpRow(row, uid, occurrenceId) {
  const isOccurrence = row?.scope === 'occurrence' || !!occurrenceId;
  return {
    ...row,
    _rsvpUid: uid,
    _rsvpOccurrenceId: occurrenceId || null,
    _rsvpScope: isOccurrence ? 'occurrence' : 'series',
    _rsvpSourceLabel: isOccurrence ? 'This date' : 'Series default',
  };
}

function buildPerUserRows(rows) {
  const perUser = new Map();
  (rows || []).forEach((rawRow) => {
    const uid = normalizeRsvpUid(rawRow);
    if (!uid) return;
    const occurrenceId = normalizeRsvpOccurrenceId(rawRow);
    const row = decorateRsvpRow(rawRow, uid, occurrenceId);
    const existing = perUser.get(uid) || {
      series: null,
      occurrenceById: new Map(),
      fallback: null,
    };
    if (row._rsvpScope === 'occurrence' && occurrenceId) {
      existing.occurrenceById.set(
        occurrenceId,
        pickLatestByUpdatedAt(existing.occurrenceById.get(occurrenceId), row)
      );
    } else {
      existing.series = pickLatestByUpdatedAt(existing.series, row);
    }
    existing.fallback = pickLatestByUpdatedAt(existing.fallback, row);
    perUser.set(uid, existing);
  });
  return perUser;
}

export function resolveEffectiveRsvpRows({
  rows,
  occurrenceId = null,
  isRecurring = false,
  seriesResponsesEnabled = true,
}) {
  const hasOccurrenceContext = isRecurring && !!occurrenceId;
  const allowSeriesResponses = !isRecurring || seriesResponsesEnabled !== false;
  const perUser = buildPerUserRows(rows);
  const resolved = [];

  perUser.forEach((entry) => {
    let chosen = null;
    if (hasOccurrenceContext) {
      chosen = entry.occurrenceById.get(occurrenceId) || (allowSeriesResponses ? entry.series : null) || null;
    } else if (isRecurring) {
      chosen = allowSeriesResponses ? entry.series || null : null;
    } else {
      chosen = entry.series || entry.fallback || null;
    }
    if (chosen) resolved.push(chosen);
  });

  return resolved;
}

export function resolveRsvpSummary({
  rows,
  userId,
  occurrenceId = null,
  isRecurring = false,
  seriesResponsesEnabled = true,
}) {
  const effectiveRows = resolveEffectiveRsvpRows({
    rows,
    occurrenceId,
    isRecurring,
    seriesResponsesEnabled,
  });
  const targetUid = typeof userId === 'string' ? userId.trim() : '';
  let yesCount = 0;
  let myEffective = null;
  effectiveRows.forEach((row) => {
    if (row?.status === 'yes') yesCount += 1;
    if (targetUid && row._rsvpUid === targetUid) myEffective = row;
  });

  const perUser = buildPerUserRows(rows);
  const mine = targetUid ? perUser.get(targetUid) : null;
  const occurrenceRow =
    isRecurring && occurrenceId && mine?.occurrenceById
      ? mine.occurrenceById.get(occurrenceId) || null
      : null;
  const seriesRow = mine?.series || null;
  const status = myEffective?.status || null;
  const scope = myEffective?._rsvpScope || null;

  return {
    yesCount,
    status,
    scope,
    occurrenceStatus: occurrenceRow?.status || null,
    seriesStatus: seriesRow?.status || null,
    effectiveRows,
  };
}

export function getRsvpYesMemberIdsForOccurrence({
  rows,
  occurrenceId = null,
  isRecurring = false,
  seriesResponsesEnabled = true,
}) {
  return resolveEffectiveRsvpRows({
    rows,
    occurrenceId,
    isRecurring,
    seriesResponsesEnabled,
  })
    .filter((row) => row?.status === 'yes' && row._rsvpUid)
    .map((row) => row._rsvpUid);
}
